import {
  Box,
  Button,
  Drawer,
  DrawerBody,
  DrawerCloseButton,
  DrawerContent,
  DrawerFooter,
  DrawerHeader,
  DrawerOverlay,
  IconButton,
  useDisclosure,
} from '@chakra-ui/react'
import { DataTypeConfigUtils, ListDataTypeConfig } from '@helo/cms'
import { Assert, pipe } from '@helo/utils'
import { isEqual } from 'lodash'
import { useEffect, useMemo, useRef, useState } from 'react'
import SimpleBar from 'simplebar-react'

import { ControlsIcon } from '../../components/Icons/ControlsIcon'
import { TableStateEditor } from '../../components/TableStateEditor'
import { Tooltip } from '../../components/Tooltip/Tooltip'
import { useOnChange } from '../../hooks/useOnChange'
import { useAppStore } from '../../store/appStore/appStore'
import {
  ColumnState,
  TableColumnConfig,
  TableStateUtils,
} from './tablewidget.parts'

export type TableStateEditorDrawerProps = {
  value: ColumnState[]
  defaultValue: ColumnState[]
  onChange?: (value: ColumnState[]) => void
  listDatatypeConfig: ListDataTypeConfig
}

export const TableStateEditorDrawer = ({
  value,
  defaultValue: propsDefaultValue,
  onChange,
  listDatatypeConfig,
}: TableStateEditorDrawerProps) => {
  const { isOpen, onOpen, onClose } = useDisclosure()
  const btnRef = useRef<HTMLButtonElement>(null)

  const hasRole = useAppStore(store => store.auth.hasRole)

  // Extract column configs from listDatatypeConfig and filter by role permissions
  const columnConfigs = useMemo(() => {
    const allColumnConfigs = pipe(
      listDatatypeConfig,
      TableStateUtils.getTableState(),
    ).columnConfigs

    // Filter out columns that the user doesn't have role permissions for
    return allColumnConfigs.filter((columnConfig: TableColumnConfig) =>
      hasRole(columnConfig.config?.state?.roles),
    )
  }, [listDatatypeConfig, hasRole])

  // Filter the default value to only include columns that the user has access to
  const defaultValue = useMemo(() => {
    return propsDefaultValue.filter(col =>
      columnConfigs.find(config => config.key === col.key),
    )
  }, [propsDefaultValue, columnConfigs])

  // Filter the value prop to only include columns that the user has access to
  const filteredValue = useMemo(() => {
    return value.filter(col =>
      columnConfigs.find(config => config.key === col.key),
    )
  }, [value, columnConfigs])

  const [tableState, setTableState] = useState<ColumnState[]>(filteredValue)

  // Extract objectDatatypeConfig from listDatatypeConfig.ofType
  const objectDataTypeConfig = useMemo(
    () =>
      pipe(
        listDatatypeConfig,
        DataTypeConfigUtils.getOfType(),
        Assert.orThrow(DataTypeConfigUtils.isKind('OBJECT')),
      ),
    [listDatatypeConfig],
  )

  useOnChange(() => {
    setTableState(filteredValue)
  }, [filteredValue])

  useEffect(() => {
    if (!isOpen) {
      setTableState(filteredValue)
    }
  }, [isOpen, filteredValue])

  const isTableStateChanged = !isEqual(value, defaultValue)

  const handleApply = () => {
    onChange?.(tableState)
    onClose()
  }

  return (
    <>
      <Tooltip
        label={
          isTableStateChanged
            ? 'Manage Columns - changes applied'
            : 'Manage Columns'
        }
        placement="top"
      >
        <Box position="relative">
          <IconButton
            ref={btnRef}
            onClick={onOpen}
            variant="controlButton"
            aria-label="Refresh"
            data-testid="table-state-editor-open-button"
            fontSize="12px"
            icon={<ControlsIcon boxSize="16px" m="-2px" />}
          />
          {/* {isDefaultValueChanged && (
            <Box
              position="absolute"
              top="-4.5px"
              right="-4.5px"
              borderRadius="full"
              bgColor="presets.turquoise.40"
              width="9px"
              height="9px"
            />
          )} */}
        </Box>
      </Tooltip>
      <Drawer
        isOpen={isOpen}
        placement="right"
        onClose={onClose}
        finalFocusRef={btnRef}
        size="sm"
      >
        <DrawerOverlay />
        <DrawerContent data-testid="table-state-editor-drawer">
          <DrawerHeader
            display="flex"
            alignItems="center"
            gap="12px"
            px="16px"
            py="16px"
            pr="48px"
          >
            Manage Columns
            <Button
              variant="tertiary"
              data-testid="table-state-editor-reset-button"
              onClick={() => {
                onChange?.(defaultValue)
                onClose()
              }}
            >
              Reset to Default
            </Button>
          </DrawerHeader>
          <DrawerCloseButton />
          <DrawerBody px="16px" py="16px">
            <SimpleBar style={{ height: '100%' }}>
              <TableStateEditor
                columnConfigs={columnConfigs}
                objectDataTypeConfig={objectDataTypeConfig}
                value={tableState}
                onChange={setTableState}
              />
            </SimpleBar>
          </DrawerBody>
          <DrawerFooter p="16px">
            <Button
              data-testid="table-state-editor-apply-button"
              onClick={handleApply}
            >
              Apply
            </Button>
          </DrawerFooter>
        </DrawerContent>
      </Drawer>
    </>
  )
}
